import { useTheme } from "@mui/material";
import { useMemo } from "react";

import { assertNever } from "@/common/ts/typeHelpers";
import { VehicleStatus } from "@/core/api/generated/v1";

import { StatusDisplayProps } from "../../../../Status/StatusDisplay";
import { StatusIndicator, StatusIndicatorProps } from "../../../../Status/StatusIndicator";

interface Props {
  status: VehicleStatus | undefined;
  indicatorProps?: Partial<StatusIndicatorProps>;
  sx?: StatusDisplayProps["sx"];
}

export function VehicleStatusIndicator({ status, indicatorProps, sx }: Props) {
  const theme = useTheme();

  const color = useMemo(() => {
    if (!status) {
      return theme.palette.divider;
    }

    switch (status) {
      case VehicleStatus.None:
      case VehicleStatus.Unknown:
        return theme.palette.divider;
      case VehicleStatus.Available:
        return theme.palette.success.main;
      case VehicleStatus.Reserved:
      case VehicleStatus.InUse:
      case VehicleStatus.OnRent:
      case VehicleStatus.InLease:
      case VehicleStatus.InSubscription:
      case VehicleStatus.Returning:
      case VehicleStatus.LeaseEnded:
        return theme.palette.info.main;
      case VehicleStatus.InWash:
      case VehicleStatus.ChangingTires:
      case VehicleStatus.InWheelService:
      case VehicleStatus.UnderMaintenance:
      case VehicleStatus.InRepair:
      case VehicleStatus.InTransit:
        return theme.palette.warning.main;
      case VehicleStatus.Disposed:
      case VehicleStatus.Sold:
      case VehicleStatus.PermanentlyLost:
      case VehicleStatus.Locked:
        return theme.palette.error.main;
      default:
        assertNever(status);
    }
  }, [theme, status]);

  return <StatusIndicator color={color} size='small' {...indicatorProps} sx={sx} />;
}
